import React, { useState } from "react";
import { Button } from "@component/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@component/card";
import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { XIcon, FileTextIcon, SendIcon } from "lucide-react";
import { ContractOffer } from "@type/contract";

interface RejectOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (decline_reason: string, message: string, successCallback: () => void) => void;
  contract: ContractOffer;
}

export function RejectOfferModal({
  isOpen,
  onClose,
  onConfirm,
  contract,
}: RejectOfferModalProps) {
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [isLoading, setLoading] = useState<boolean>(false);

  if (!isOpen || !contract) return null;

  const handleConfirm = () => {
    setLoading(true);
    onConfirm(reason, message, () => {
      setLoading(false);
      setMessage("");
      setReason("");
    });

  };

  const handleCancel = () => {
    setMessage("");
    setReason("");
    onClose();
  };

  const reasonOptions = [
    "Accepted another offer",
    "Salary doesn't meet expectations",
    "Role doesn't align with career goals",
    "Location/commute issues",
    "Benefits package is insufficient",
    "Work environment/culture concerns",
    "Personal reasons",
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="relative w-full max-w-md">
        <Card className="w-full shadow-lg bg-white">
          <CardHeader className="pb-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileTextIcon className="h-6 w-6 text-red-500" />
                <CardTitle className="text-xl font-bold text-gray-900">
                  Decline Job Offer
                </CardTitle>
              </div>
              <Button
                disabled={isLoading}
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                className="h-8 w-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
            <CardDescription className="text-gray-600">
              Are you sure you want to decline the offer from{" "}
              <span className="font-bold">{contract.company_name}</span> for the{" "}
              <span className="font-bold">{contract.job_title}</span> position?
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label
                htmlFor="decline-reason"
                className="text-sm font-medium block mb-2 text-gray-800"
              >
                Reason for declining
              </Label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select a reason</option>
                {reasonOptions.map((option, index) => (
                  <option key={option} value={option} id={`oki3xk_${index}`}>
                    {option}
                  </option>
                ))}
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label
                htmlFor="decline-message"
                className="text-sm font-medium block mb-2 text-gray-800"
              >
                Message to company (optional)
              </Label>
              <Textarea
                placeholder="Thank you for the opportunity to interview with your company..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[120px] bg-white border-gray-300 text-gray-800 placeholder-gray-400"
              />

              <p className="text-xs text-gray-500 mt-2">
                Your message will be sent to the hiring team at{" "}
                {contract.company_name}.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
              <p className="font-medium mb-1">Important note:</p>
              <p>
                Declining this offer is final and cannot be undone. The company
                will be notified of your decision.
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2 pt-0 border-t border-gray-100 p-4">
            <Button
              disabled={isLoading}
              variant="outline"
              onClick={handleCancel}
              className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1"
              onClick={handleConfirm}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
                  Processing...
                </>
              ) : (
                <>
                  <SendIcon className="h-4 w-4" />
                  Confirm & Send
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
